/**
 * The Compare page's motivational read: what you have been doing, and what the
 * two of you are worth TOGETHER.
 *
 * Everything here is derived from data the page already has — the history
 * series behind the chart, and the lead lists behind the trade panes — so it
 * costs no extra round trip. Pure, so the arithmetic can be argued with in a
 * test rather than squinted at on a card.
 */

export interface DaySample {
  day: string;
  count: number;
}

export interface BestRun {
  /** Total added across the window. */
  gained: number;
  /** First and last day of the run, for a caption. */
  from: string;
  to: string;
}

/**
 * The best any N-day stretch of this series managed.
 *
 * "Best week" is the personal record a trainer actually recognises — not a
 * calendar week, a ROLLING one, because a Community Day on a Saturday and a
 * raid hour on the following Wednesday are one good week to the person who
 * played them and two mediocre ones to a calendar.
 *
 * Returns null when the series is too short to contain a full window: a "best
 * week" measured over three days is a claim about a week that never happened.
 */
export function bestRun(series: readonly DaySample[], days = 7): BestRun | null {
  if (series.length < 2 || days < 1) return null;
  const sorted = [...series].sort((a, b) => a.day.localeCompare(b.day));
  let best: BestRun | null = null;
  for (let end = 0; end < sorted.length; end++) {
    for (let start = 0; start <= end; start++) {
      const span = dayGap(sorted[start].day, sorted[end].day);
      if (span > days - 1) continue;
      const gained = sorted[end].count - sorted[start].count;
      if (gained <= 0) continue;
      if (!best || gained > best.gained) {
        best = { gained, from: sorted[start].day, to: sorted[end].day };
      }
    }
  }
  return best;
}

/** Whole days between two YYYY-MM-DD keys. UTC, so it never drifts an hour. */
export function dayGap(from: string, to: string): number {
  const a = Date.parse(`${from}T00:00:00.000Z`);
  const b = Date.parse(`${to}T00:00:00.000Z`);
  if (!Number.isFinite(a) || !Number.isFinite(b)) return 0;
  return Math.round((b - a) / 86_400_000);
}

/**
 * Days in a row, ending today, on which the count went UP.
 *
 * Counted backwards from the latest reading and stopped by the first flat or
 * missing day, because that is what a streak is. A quiet day does not "carry"
 * here the way it does on the chart: the chart is answering "what did your dex
 * look like", where carrying forward is honest, and this is answering "did you
 * add something", where it would be a lie.
 */
export function currentStreak(series: readonly DaySample[]): number {
  if (series.length < 2) return 0;
  const sorted = [...series].sort((a, b) => a.day.localeCompare(b.day));
  let streak = 0;
  for (let i = sorted.length - 1; i > 0; i--) {
    if (dayGap(sorted[i - 1].day, sorted[i].day) !== 1) break;
    if (sorted[i].count <= sorted[i - 1].count) break;
    streak += 1;
  }
  return streak;
}

export interface Pace {
  /** Entries per day over the window, as measured. */
  perDay: number;
  /** Days to close `remaining` at that rate; null when the pace is zero. */
  daysToClose: number | null;
}

/**
 * How fast this line has been moving, and what that implies for a gap.
 *
 * A PROJECTION, not a prediction, and the caller must label it so. It assumes
 * the last stretch repeats forever, which nobody's play does — the value is in
 * the comparison ("at this rate, four months for you and seven for them"), not
 * in the absolute number.
 *
 * Null days-to-close when the pace is zero: "never" is the truthful answer and
 * Infinity is not something to print on a card.
 */
export function paceFrom(series: readonly DaySample[], remaining: number): Pace {
  if (series.length < 2) return { perDay: 0, daysToClose: null };
  const sorted = [...series].sort((a, b) => a.day.localeCompare(b.day));
  const span = dayGap(sorted[0].day, sorted[sorted.length - 1].day);
  const gained = sorted[sorted.length - 1].count - sorted[0].count;
  if (span <= 0 || gained <= 0) return { perDay: 0, daysToClose: null };
  const perDay = gained / span;
  if (remaining <= 0) return { perDay, daysToClose: 0 };
  return { perDay, daysToClose: Math.ceil(remaining / perDay) };
}

/** "4 months" / "3 weeks" / "5 days" — a duration a person would say out loud. */
export function humanDays(days: number): string {
  if (!Number.isFinite(days) || days <= 0) return "no time";
  if (days < 14) return `${Math.round(days)} day${Math.round(days) === 1 ? "" : "s"}`;
  if (days < 60) return `${Math.round(days / 7)} weeks`;
  if (days < 730) return `${Math.round(days / 30)} months`;
  return `${(days / 365).toFixed(1)} years`;
}

export interface TogetherTotals {
  /** Species at least one of you has. */
  union: number;
  /** How many of their gaps I could fill, and vice versa. */
  iCanFill: number;
  theyCanFill: number;
}

/**
 * What the two of you would have BETWEEN you.
 *
 * The most quietly motivating number on the page, because it is the only one
 * where the other trainer is an asset rather than an opponent — and it is the
 * argument for the trade flow, stated as a fact rather than a prompt.
 *
 * `mineCount` is my own total; `iHaveTheyDont` / `theyHaveIDont` are the two
 * lead lists the page already computes. The union is therefore mine plus
 * everything of theirs I am missing, with no double counting.
 */
export function togetherTotals(
  mineCount: number,
  iHaveTheyDont: number,
  theyHaveIDont: number,
): TogetherTotals {
  return {
    union: Math.max(0, mineCount) + Math.max(0, theyHaveIDont),
    iCanFill: Math.max(0, iHaveTheyDont),
    theyCanFill: Math.max(0, theyHaveIDont),
  };
}

export interface RegionGap {
  region: string;
  /** Species they have here that I do not. */
  behind: number;
}

/**
 * Where I am furthest behind, by region.
 *
 * Turns "I am 151 behind" into "I am 151 behind IN HOENN", which is the
 * difference between a fact and a plan. Built from the leads they hold over me,
 * so it only ever names a region where something is actually gettable.
 */
export function regionGaps(
  theyHaveIDont: ReadonlyArray<{ id: number }>,
  regionOf: (id: number) => string,
  limit = 3,
): RegionGap[] {
  const counts = new Map<string, number>();
  for (const row of theyHaveIDont) {
    const region = regionOf(row.id);
    if (!region) continue;
    counts.set(region, (counts.get(region) ?? 0) + 1);
  }
  return [...counts.entries()]
    .map(([region, behind]) => ({ region, behind }))
    // Ties break by name so the list is stable between renders.
    .sort((a, b) => b.behind - a.behind || a.region.localeCompare(b.region))
    .slice(0, Math.max(0, limit));
}
